/**
 * Assign every opaque pixel of a motif to the thread its design color maps to.
 *
 * Deliberately canvas-free. The web test environment stubs `getContext` with a
 * handful of drawing methods and provides no `getImageData`, and jsdom has no
 * `ImageData` constructor at all — a test could neither read a canvas back nor
 * build an input for one. Keeping the pixel logic pure means it is testable with
 * a plain object, while `renderEmbroideryPreview` stays a thin drawing layer.
 */

import type { PaletteEntry, ThreadColor } from '@designdeck/shared';
import { hexToRgb, rgbDistanceSq, threadById } from '@designdeck/shared';

/** Structurally satisfied by `CanvasRenderingContext2D.getImageData()`. */
export interface PixelData {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export interface ThreadMask {
  threadId: string;
  hex: string;
  metallic: boolean;
  /** One byte per pixel, 1 where this thread covers the motif. */
  mask: Uint8Array;
  pixelCount: number;
}

/** Alpha below this is background and belongs to no thread. */
export const ALPHA_THRESHOLD = 128;

/**
 * One mask per distinct thread, in descending coverage order so the renderer
 * paints the largest areas first. Threads no pixel resolves to are left out.
 */
export function mapPixelsToThreads(
  pixels: PixelData,
  palette: PaletteEntry[],
  mapping: Record<string, string>,
  threads: ThreadColor[]
): ThreadMask[] {
  const { data, width, height } = pixels;
  const pixelCount = width * height;

  if (palette.length === 0 || pixelCount === 0) return [];

  // Resolve palette colors and their threads once — this loop runs per pixel.
  const resolved = palette.map((entry) => {
    const threadId = mapping[entry.hex] ?? entry.suggestedThreadId;
    const thread = threadById(threadId, threads);
    return {
      rgb: hexToRgb(entry.hex),
      threadId,
      hex: thread?.hex ?? entry.hex,
      metallic: thread?.metallic ?? false,
    };
  });

  // Non-empty by the guard above; naming it lets the loop stay branch-free.
  const firstResolved = resolved[0];
  if (!firstResolved) return [];

  const masks = new Map<string, ThreadMask>();

  for (let i = 0, p = 0; p < pixelCount; i += 4, p++) {
    if ((data[i + 3] ?? 0) < ALPHA_THRESHOLD) continue;

    const pixel = {
      r: data[i] ?? 0,
      g: data[i + 1] ?? 0,
      b: data[i + 2] ?? 0,
    };

    let nearest = firstResolved;
    let bestDistance = Infinity;
    for (const candidate of resolved) {
      const distance = rgbDistanceSq(pixel, candidate.rgb);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = candidate;
      }
    }

    let entry = masks.get(nearest.threadId);
    if (!entry) {
      entry = {
        threadId: nearest.threadId,
        hex: nearest.hex,
        metallic: nearest.metallic,
        mask: new Uint8Array(pixelCount),
        pixelCount: 0,
      };
      masks.set(nearest.threadId, entry);
    }
    entry.mask[p] = 1;
    entry.pixelCount++;
  }

  return Array.from(masks.values()).sort((a, b) => b.pixelCount - a.pixelCount);
}
